import React, { useMemo, useState } from 'react';
import { Button, DatePicker, Empty, Form, Input, Popconfirm, Space, Tag, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { REASON_OPTIONS } from '../constants';
import type { AttributionRecord, WarehouseScoreItem } from '../types';
import { getUserInfo } from '../../../../utils/auth';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';
import { showError } from '../../../../utils/exceptionUtils';

const { TextArea } = Input;
const { Text } = Typography;

const OTHER_TAG_KEY = 'other';

interface CreatePayload {
    warehouseId: number;
    statDate: string;
    reason: string;
    notes: string;
    expectedRecoveryDate: string;
}

interface UpdatePayload {
    id: number;
    reason: string;
    notes: string;
    expectedRecoveryDate: string;
}

interface Props {
    warehouse: WarehouseScoreItem;
    records: AttributionRecord[];
    submitting?: boolean;
    onCreate: (payload: CreatePayload) => Promise<unknown>;
    onUpdate: (payload: UpdatePayload) => Promise<unknown>;
    onDelete: (id: number) => Promise<unknown>;
}

interface ReasonFieldState {
    selectedKey: string | null;
    otherText: string;
}

const buildReasonState = (
    reason: string | undefined,
    tagLabels: { key: string; label: string }[],
): ReasonFieldState => {
    if (!reason) return { selectedKey: null, otherText: '' };
    const matched = tagLabels.find((t) => t.key !== OTHER_TAG_KEY && t.label === reason);
    if (matched) return { selectedKey: matched.key, otherText: '' };
    return { selectedKey: OTHER_TAG_KEY, otherText: reason };
};

const formatCreatedAt = (iso?: string) => (iso ? dayjs(iso).format('YYYY-MM-DD HH:mm') : '-');

const AttributionForm: React.FC<Props> = ({ warehouse, records, submitting, onCreate, onUpdate, onDelete }) => {
    const { t } = useTranslation();
    const I18N = 'dashboard.dailyMeeting';

    const currentUserId = useMemo(() => {
        const raw = getUserInfo()?.account?.id;
        const parsed = raw ? Number(raw) : NaN;
        return Number.isFinite(parsed) ? parsed : undefined;
    }, []);
    const canAdmin = hasResource([ResourceCode.dashboard.DAILY_MEETING_ATTRIBUTION_ADMIN]);
    const canWrite = hasResource([ResourceCode.dashboard.DAILY_MEETING_ATTRIBUTION_WRITE]) || canAdmin;

    const tagLabels = useMemo(
        () =>
            REASON_OPTIONS.map((option) => ({
                key: option.i18nKey,
                label: t(`${I18N}.reason.${option.i18nKey}`) as string,
            })),
        [t],
    );

    const sortedRecords = useMemo(
        () => [...records].sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '')),
        [records],
    );

    return (
        <div className="flex flex-col gap-3">
            <AttributionBubbleList
                records={sortedRecords}
                tagLabels={tagLabels}
                currentUserId={currentUserId}
                canAdmin={canAdmin}
                onUpdate={onUpdate}
                onDelete={onDelete}
                submitting={submitting}
            />
            {canWrite && (
                <NewAttributionForm
                    warehouse={warehouse}
                    tagLabels={tagLabels}
                    submitting={submitting}
                    onCreate={onCreate}
                />
            )}
        </div>
    );
};

interface BubbleListProps {
    records: AttributionRecord[];
    tagLabels: { key: string; label: string }[];
    currentUserId?: number;
    canAdmin: boolean;
    onUpdate: Props['onUpdate'];
    onDelete: Props['onDelete'];
    submitting?: boolean;
}

const AttributionBubbleList: React.FC<BubbleListProps> = ({
    records,
    tagLabels,
    currentUserId,
    canAdmin,
    onUpdate,
    onDelete,
    submitting,
}) => {
    const { t } = useTranslation();
    const I18N = 'dashboard.dailyMeeting';

    if (records.length === 0) {
        return <Empty description={t(`${I18N}.attribution.emptyBubble`)} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    return (
        <div className="flex flex-col gap-2">
            {records.map((record) => (
                <AttributionBubble
                    key={record.id}
                    record={record}
                    tagLabels={tagLabels}
                    currentUserId={currentUserId}
                    canAdmin={canAdmin}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    submitting={submitting}
                />
            ))}
        </div>
    );
};

interface BubbleProps {
    record: AttributionRecord;
    tagLabels: { key: string; label: string }[];
    currentUserId?: number;
    canAdmin: boolean;
    onUpdate: Props['onUpdate'];
    onDelete: Props['onDelete'];
    submitting?: boolean;
}

const AttributionBubble: React.FC<BubbleProps> = ({
    record,
    tagLabels,
    currentUserId,
    canAdmin,
    onUpdate,
    onDelete,
    submitting,
}) => {
    const { t } = useTranslation();
    const I18N = 'dashboard.dailyMeeting';
    const [editing, setEditing] = useState(false);
    const initial = useMemo(() => buildReasonState(record.reason, tagLabels), [record.reason, tagLabels]);
    const [reasonState, setReasonState] = useState<ReasonFieldState>(initial);
    const [notes, setNotes] = useState(record.notes ?? '');
    const [expectedDate, setExpectedDate] = useState<Dayjs | null>(
        record.expectedRecoveryDate ? dayjs(record.expectedRecoveryDate) : null,
    );

    const canEdit = canAdmin || (currentUserId != null && record.creatorId === currentUserId);
    const wasEdited = record.updatedById != null && record.updatedById !== record.creatorId;

    const handleSave = async () => {
        const resolvedReason =
            reasonState.selectedKey === OTHER_TAG_KEY
                ? reasonState.otherText.trim()
                : tagLabels.find((tag) => tag.key === reasonState.selectedKey)?.label ?? '';
        if (!resolvedReason) {
            message.warning(t(`${I18N}.attribution.reasonRequired`) as string);
            return;
        }
        const trimmedNotes = notes.trim();
        if (!trimmedNotes) {
            message.warning(t(`${I18N}.attribution.notesRequired`) as string);
            return;
        }
        if (!expectedDate) {
            message.warning(t(`${I18N}.attribution.expectedRecoveryDateRequired`) as string);
            return;
        }
        try {
            await onUpdate({
                id: record.id,
                reason: resolvedReason,
                notes: trimmedNotes,
                expectedRecoveryDate: expectedDate.format('YYYY-MM-DD'),
            });
            setEditing(false);
        } catch (e) {
            showError(e);
        }
    };

    const handleCancel = () => {
        setReasonState(initial);
        setNotes(record.notes ?? '');
        setExpectedDate(record.expectedRecoveryDate ? dayjs(record.expectedRecoveryDate) : null);
        setEditing(false);
    };

    const handleDelete = async () => {
        try {
            await onDelete(record.id);
        } catch (e) {
            showError(e);
        }
    };

    if (editing) {
        return (
            <div className="rounded-lg border border-blue-100 bg-blue-50/40 px-3 py-2.5">
                <ReasonTagPicker tagLabels={tagLabels} value={reasonState} onChange={setReasonState} />
                <div className="mt-2">
                    <TextArea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        maxLength={500}
                        showCount
                        placeholder={t(`${I18N}.attribution.notesPlaceholder`) as string}
                    />
                </div>
                <div className="mt-2 flex items-center gap-2">
                    <Text type="secondary" className="text-xs whitespace-nowrap">
                        {t(`${I18N}.attribution.expectedRecoveryDate`)}
                    </Text>
                    <DatePicker
                        value={expectedDate}
                        onChange={(v) => setExpectedDate(v)}
                        allowClear={false}
                        style={{ width: 180 }}
                    />
                </div>
                <div className="mt-2 flex justify-end gap-2">
                    <Button size="small" onClick={handleCancel}>{t(`${I18N}.attribution.cancel`)}</Button>
                    <Button size="small" type="primary" loading={submitting} onClick={handleSave}>
                        {t(`${I18N}.attribution.save`)}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-gray-100 bg-white px-3 py-2.5">
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        {record.reason && <Tag color="processing">{record.reason}</Tag>}
                        {record.expectedRecoveryDate && (
                            <Text type="secondary" className="text-xs">
                                {t(`${I18N}.attribution.expectedRecoveryDate`)}: {record.expectedRecoveryDate}
                            </Text>
                        )}
                    </div>
                    <div className="text-sm whitespace-pre-wrap break-words mt-1.5">
                        {record.notes ?? '-'}
                    </div>
                    <div className="text-xs text-gray-400 mt-1.5">
                        {record.creatorName ?? '-'} · {formatCreatedAt(record.createdAt)}
                        {wasEdited && record.updatedByName && (
                            <>
                                {' · '}
                                {t(`${I18N}.attribution.editedByAt`, {
                                    editor: record.updatedByName,
                                    time: formatCreatedAt(record.updatedAt),
                                })}
                            </>
                        )}
                    </div>
                </div>
                {canEdit && (
                    <span className="flex items-center gap-2 flex-shrink-0">
                        <a className="text-sm text-blue-500" onClick={() => setEditing(true)}>
                            {t(`${I18N}.attribution.edit`)}
                        </a>
                        <Popconfirm
                            title={t(`${I18N}.attribution.deleteConfirmTitle`) as string}
                            onConfirm={handleDelete}
                            okButtonProps={{ loading: submitting }}
                        >
                            <a className="text-sm text-red-500">{t(`${I18N}.attribution.delete`)}</a>
                        </Popconfirm>
                    </span>
                )}
            </div>
        </div>
    );
};

interface NewFormProps {
    warehouse: WarehouseScoreItem;
    tagLabels: { key: string; label: string }[];
    submitting?: boolean;
    onCreate: Props['onCreate'];
}

const NewAttributionForm: React.FC<NewFormProps> = ({ warehouse, tagLabels, submitting, onCreate }) => {
    const { t } = useTranslation();
    const I18N = 'dashboard.dailyMeeting';
    const [reasonState, setReasonState] = useState<ReasonFieldState>({ selectedKey: null, otherText: '' });
    const [form] = Form.useForm<{ notes: string; expectedRecoveryDate: Dayjs }>();

    const handleFinish = async (values: { notes: string; expectedRecoveryDate: Dayjs }) => {
        const resolvedReason =
            reasonState.selectedKey === OTHER_TAG_KEY
                ? reasonState.otherText.trim()
                : tagLabels.find((tag) => tag.key === reasonState.selectedKey)?.label ?? '';
        if (!resolvedReason) {
            message.warning(t(`${I18N}.attribution.reasonRequired`) as string);
            return;
        }
        try {
            await onCreate({
                warehouseId: warehouse.warehouseId,
                statDate: warehouse.statDate,
                reason: resolvedReason,
                notes: values.notes.trim(),
                expectedRecoveryDate: values.expectedRecoveryDate.format('YYYY-MM-DD'),
            });
            form.resetFields();
            setReasonState({ selectedKey: null, otherText: '' });
        } catch (e) {
            showError(e);
        }
    };

    return (
        <div className="rounded-lg border border-gray-100 bg-gray-50/40 px-3 py-3">
            <div className="text-sm font-medium mb-2">{t(`${I18N}.attribution.addTitle`)}</div>
            <ReasonTagPicker tagLabels={tagLabels} value={reasonState} onChange={setReasonState} />
            <Form form={form} layout="vertical" onFinish={handleFinish} className="mt-2">
                <Form.Item
                    name="notes"
                    label={t(`${I18N}.attribution.notes`) || undefined}
                    rules={[{ required: true, message: t(`${I18N}.attribution.notesRequired`) || undefined }]}
                >
                    <TextArea
                        rows={3}
                        maxLength={500}
                        showCount
                        placeholder={t(`${I18N}.attribution.notesPlaceholder`) || undefined}
                    />
                </Form.Item>
                <Form.Item
                    name="expectedRecoveryDate"
                    label={t(`${I18N}.attribution.expectedRecoveryDate`) || undefined}
                    rules={[{ required: true, message: t(`${I18N}.attribution.expectedRecoveryDateRequired`) || undefined }]}
                >
                    <DatePicker style={{ width: 220 }} />
                </Form.Item>
                <Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={submitting}>
                            {t(`${I18N}.attribution.submit`)}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </div>
    );
};

interface ReasonPickerProps {
    tagLabels: { key: string; label: string }[];
    value: ReasonFieldState;
    onChange: (next: ReasonFieldState) => void;
}

const ReasonTagPicker: React.FC<ReasonPickerProps> = ({ tagLabels, value, onChange }) => {
    const { t } = useTranslation();
    const I18N = 'dashboard.dailyMeeting';
    return (
        <>
            <Text type="secondary" className="text-xs">{t(`${I18N}.attribution.reason`)}</Text>
            <div className="mt-1 flex flex-wrap gap-1.5">
                {tagLabels.map((tag) => {
                    const active = value.selectedKey === tag.key;
                    return (
                        <Tag.CheckableTag
                            key={tag.key}
                            checked={active}
                            onChange={(checked) => onChange({
                                selectedKey: checked ? tag.key : null,
                                otherText: tag.key === OTHER_TAG_KEY && checked ? value.otherText : '',
                            })}
                        >
                            {tag.label}
                        </Tag.CheckableTag>
                    );
                })}
            </div>
            {value.selectedKey === OTHER_TAG_KEY && (
                <div className="mt-2">
                    <Input
                        value={value.otherText}
                        onChange={(e) => onChange({ selectedKey: OTHER_TAG_KEY, otherText: e.target.value })}
                        maxLength={100}
                        showCount
                        placeholder={t(`${I18N}.attribution.otherPlaceholder`) || undefined}
                    />
                </div>
            )}
        </>
    );
};

export default AttributionForm;
